import React, { useMemo, useEffect, useState } from "react";
import { Download } from "react-feather";
import { API_BASE_URL } from "../../config/api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./billing.css";

const TransactionHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) throw new Error("No authentication token found");

        const response = await fetch(`${API_BASE_URL}/api/transactions`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setTransactions(Array.isArray(data) ? data : []);
      } catch (err) {
        setError("Failed to load transaction history. Please try again later.");
        console.error("Error fetching transactions:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, []);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatType = (type = "") => {
    return String(type)
      .split("_")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  const typeBadgeClass = (type = "") => {
    const t = String(type).toLowerCase();
    if (t.includes("credit") || t.includes("purchase") || t.includes("paid")) return "success";
    if (t.includes("invoice") || t.includes("subscription") || t.includes("plan")) return "info";
    if (t.includes("fail") || t.includes("refund") || t.includes("chargeback")) return "warn";
    return "";
  };

  const sortedTransactions = useMemo(() => {
    const copy = [...transactions];
    copy.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return copy;
  }, [transactions]);

  const handleDownloadInvoice = async (txId) => {
    setDownloadingId(txId);
    setDownloadError(null);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${API_BASE_URL}/api/transactions/${txId}/invoice`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const invoice = await response.json();

      const doc = new jsPDF();
      doc.setProperties({
        title: `Invoice ${invoice.invoiceNumber}`,
        author: "Nexopta",
        creator: "Nexopta System",
      });

      doc.setFillColor(25, 40, 65);
      doc.rect(0, 0, 210, 40, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont("helvetica", "bold");
      doc.text("INVOICE", 105, 25, { align: "center" });

      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Date: ${new Date().toLocaleDateString()}`, 105, 32, { align: "center" });

      let y = 50;

      doc.setTextColor(0, 0, 0);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Invoice Details", 20, y);
      y += 10;

      doc.setDrawColor(200, 200, 200);
      doc.line(20, y - 5, 190, y - 5);

      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");

      const details = [
        { label: "Invoice Number:", value: invoice.invoiceNumber },
        { label: "Date Issued:", value: invoice.dateIssued },
        { label: "Date Paid:", value: invoice.datePaid },
        { label: "Status:", value: String(invoice.status || "").toUpperCase() },
        { label: "Amount:", value: `$${Number(invoice.amount || 0).toFixed(2)}` },
        { label: "Amount Paid:", value: `$${Number(invoice.amountPaid || 0).toFixed(2)}` },
        { label: "Amount Due:", value: `$${Number(invoice.amountDue || 0).toFixed(2)}` },
        { label: "Customer ID:", value: invoice.customerId },
      ];

      details.forEach((item) => {
        doc.setFont("helvetica", "bold");
        doc.text(item.label, 20, y);
        doc.setFont("helvetica", "normal");
        doc.text(String(item.value ?? "N/A"), 60, y);
        y += 8;
      });

      y += 10;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("Billing Address", 20, y);
      y += 10;

      doc.line(20, y - 5, 190, y - 5);

      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");

      const billing = invoice.billingAddress && invoice.billingAddress.length > 0 ? invoice.billingAddress[0] : null;

      if (billing) {
        doc.text(billing.name || "N/A", 20, y); y += 6;
        doc.text(billing.street1 || "", 20, y); y += 6;
        doc.text(billing.street2 || "", 20, y); y += 6;
        doc.text(`${billing.city || ""}, ${billing.province || ""} ${billing.postalCode || ""}`, 20, y); y += 6;
        doc.text(billing.country || "", 20, y); y += 6;
        doc.text(`Phone: ${billing.phone || "N/A"}`, 20, y); y += 6;
        doc.text(`Email: ${billing.email || "N/A"}`, 20, y);
      } else {
        doc.text("N/A", 20, y);
      }

      y += 15;

      if (invoice.lineItems && invoice.lineItems.length > 0) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.text("Line Items", 20, y);
        y += 10;

        doc.line(20, y - 5, 190, y - 5);

        autoTable(doc, {
          startY: y,
          head: [["Description", "Quantity", "Price", "Total"]],
          body: invoice.lineItems.map((item) => [
            item.description || "N/A",
            item.quantity || 1,
            `$${Number(item.price || 0).toFixed(2)}`,
            `$${Number(item.total || 0).toFixed(2)}`,
          ]),
          theme: "grid",
          headStyles: { fillColor: [25, 40, 65], textColor: [255, 255, 255] },
          styles: { textColor: [0, 0, 0], lineColor: [200, 200, 200], lineWidth: 0.1 },
          margin: { left: 20, right: 20 },
        });

        y = doc.lastAutoTable.finalY + 15;
      }

      const taxAmount = Number(invoice?.tax?.amount || 0);
      const discountAmount = Number(invoice?.discounts?.amount || 0);
      const shippingAmount = Number(invoice?.shipping?.amount || 0);
      const amount = Number(invoice?.amount || 0);

      doc.setFont("helvetica", "bold");
      doc.text("Summary", 140, y);
      y += 10;

      doc.setFont("helvetica", "normal");
      doc.text(`Subtotal: $${(amount - taxAmount).toFixed(2)}`, 140, y); y += 8;
      doc.text(`Tax: $${taxAmount.toFixed(2)} (${invoice?.tax?.details || ""})`, 140, y); y += 8;
      doc.text(`Discount: $${discountAmount.toFixed(2)} (${invoice?.discounts?.details || ""})`, 140, y); y += 8;
      doc.text(`Shipping: $${shippingAmount.toFixed(2)}`, 140, y); y += 8;
      doc.setFont("helvetica", "bold");
      doc.text(`Total: $${amount.toFixed(2)}`, 140, y);

      const pageHeight = doc.internal.pageSize.height;
      doc.setFontSize(10);
      doc.setTextColor(150);
      doc.text("Thank you for your business!", 105, pageHeight - 20, { align: "center" });
      doc.text("Generated by SalesOptAI", 105, pageHeight - 10, { align: "center" });

      doc.save(`invoice_${invoice.invoiceNumber}.pdf`);
    } catch (err) {
      console.error("Error downloading invoice:", err);
      setDownloadError("Failed to download invoice. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="transaction-history-card">
      <div className="transaction-header">
        <div className="transaction-title">


          <div style={{ minWidth: 0 }}>
            <h3>Transactions</h3>
            <p className="transaction-subtitle">
              Invoices and payment activity for your account.
            </p>
          </div>
        </div>

        <div className="tx-meta">
          <span className="tx-count-pill">
            {loading ? "Loading…" : `${sortedTransactions.length} record${sortedTransactions.length === 1 ? "" : "s"}`}
          </span>
        </div>
      </div>

      <div className="transaction-history-content">
        {downloadError && (
          <div className="payment-method-alert error" role="alert">
            <span>{downloadError}</span>
          </div>
        )}

        {loading ? (
          <div className="transaction-empty">
            <strong>Loading transactions…</strong>
            <div>Please wait a moment.</div>
          </div>
        ) : error ? (
          <p className="error-message">{error}</p>
        ) : sortedTransactions.length === 0 ? (
          <div className="transaction-empty">
            <strong>No transactions yet</strong>
            <div>
              Your payment history will appear here once available.
            </div>
          </div>
        ) : (
          <>
          <div className="transaction-table-wrap">
            <table className="transaction-table">
              <thead>
                <tr>
                  <th style={{ width: "170px" }}>Date</th>
                  <th style={{ width: "180px" }}>Type</th>
                  <th style={{ width: "120px", textAlign: "right" }}>Amount</th>
                  <th>Transaction ID</th>
                  <th style={{ width: "140px" }}>Action</th>
                </tr>
              </thead>

              <tbody>
                {sortedTransactions.map((tx) => {
                  const badgeClass = typeBadgeClass(tx.type);
                  const amountText =
                    typeof tx.amount === "number" ? `$${tx.amount.toFixed(2)}` : "N/A";

                  const txId = tx.transaction_id || "N/A";

                  return (
                    <tr key={tx.id}>
                      <td>{formatDate(tx.timestamp)}</td>

                      <td>
                        <span className={`tx-type ${badgeClass}`}>
                          {formatType(tx.type)}
                        </span>
                      </td>

                      <td className="tx-amount">{amountText}</td>

                      <td>
                        <span className="tx-id" title={txId}>
                          {txId}
                        </span>
                      </td>

                      <td>
                        <button
                          onClick={() => handleDownloadInvoice(tx.id)}
                          className="download-btn"
                          title="Download Invoice"
                          aria-label="Download invoice"
                          disabled={downloadingId === tx.id}
                        >
                          <Download size={16} />
                          <span className="download-label">
                            {downloadingId === tx.id ? "Working…" : "Invoice"}
                          </span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile: one card per transaction. The table above is hidden
              under 640px, where its fixed column widths could only scroll. */}
          <div className="transaction-cards">
            {sortedTransactions.map((tx) => {
              const badgeClass = typeBadgeClass(tx.type);
              const amountText =
                typeof tx.amount === "number" ? `$${tx.amount.toFixed(2)}` : "N/A";
              const txId = tx.transaction_id || "N/A";

              return (
                <div key={tx.id} className="transaction-card-item">
                  <div className="transaction-card-top">
                    <span className={`tx-type ${badgeClass}`}>
                      {formatType(tx.type)}
                    </span>
                    <span className="transaction-card-amount">{amountText}</span>
                  </div>

                  <p className="transaction-card-date">{formatDate(tx.timestamp)}</p>
                  <p className="transaction-card-id">{txId}</p>

                  <div className="transaction-card-footer">
                    <button
                      onClick={() => handleDownloadInvoice(tx.id)}
                      className="download-btn"
                      title="Download Invoice"
                      aria-label="Download invoice"
                      disabled={downloadingId === tx.id}
                    >
                      <Download size={16} />
                      <span className="download-label">
                        {downloadingId === tx.id ? "Working…" : "Invoice"}
                      </span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TransactionHistory;
